import { connectionsStore, returnToLibrary, type ConnectionsState } from '../../api/connections';
import { isUsableConnection } from '../../api/connectionModel';
import { getSession } from '../../api/session';
import type { Store } from '../../lib/store';
import { reportError } from './errorReporter';

export interface ReactivationDeps {
  store: Store<ConnectionsState>;
  hasSession: () => boolean;
  returnToLibrary: () => Promise<boolean>;
  /** Runs the switch after the change that triggered it has settled (default: a macrotask). */
  schedule: (task: () => void) => void;
}

const defaults: ReactivationDeps = {
  store: connectionsStore,
  hasSession: () => getSession() !== null,
  returnToLibrary,
  schedule: (task) => void setTimeout(task, 0),
};

/**
 * Signs in again when the user switches a saved connection back on while none
 * was active (the "No active connections" gate → Manage Connections): the
 * app returns to the library with it, instead of leaving the welcome gate's
 * "Return to Library" for another tap once they go back. Only a connection
 * that was saved and deactivated counts (adding a new one signs in by
 * itself), only while signed out and no switch is running, and only when
 * it can sign in without new credentials. Returns the unsubscribe.
 */
export function returnOnReactivation(deps: ReactivationDeps = defaults): () => void {
  let previous = deps.store.get();
  return deps.store.subscribe(() => {
    const state = deps.store.get();
    const before = previous;
    previous = state;
    if (deps.hasSession() || state.switching) return;
    const noneWasActive = before.connections.length > 0 && !before.connections.some((c) => c.isActive);
    if (!noneWasActive) return;
    const reactivated = state.connections.find((c) => c.isActive && before.connections.some((b) => b.id === c.id && !b.isActive));
    if (!reactivated || !isUsableConnection(reactivated)) return;
    deps.schedule(() => {
      if (deps.hasSession() || deps.store.get().switching) return;
      deps.returnToLibrary().catch((error: unknown) => reportError(error, 'returnToLibrary', 'session'));
    });
  });
}
